/**
 * Maps the C# MigrationStatus enum values to UI-friendly status strings
 */

import { logger } from './logger';

export function mapMigrationStatus(status: string | number): string {
  try {
    // Handle numeric enum values from C# backend
    const numericStatusMap: Record<number, string> = {
      0: 'running', // Configurin
      1: 'ExportQueued',
      2: 'ExportInProgress',
      3: 'ExportComplete',
      4: 'TransformationQueued',
      5: 'TransformationInProgress',
      6: 'TransformationComplete',
      7: 'ImportQueued',
      8: 'ImportInProgress',
      9: 'ValidationFailed',
      10: 'PartiallyCompleted',
      11: 'completed', // Complete
      12: 'SnapshotCompleted', // SnapshotComplete
      13: 'failed', // Failed
      // Rollback pipeline stages — sequential from Failed (13), matching MigrationStatus.cs
      14: 'RollbackQueued',
      15: 'RollbackSetupInProgress',
      16: 'RollbackPlanAwaitingApproval',
      17: 'RollbackInProgress',
      18: 'RollbackComplete',
      19: 'RollbackPartiallyCompleted',
      20: 'RollbackFailed'
    };

    // Handle string enum values
    const stringStatusMap: Record<string, string> = {
      Configuring: 'running',
      ExportQueued: 'ExportQueued',
      ExportInProgress: 'ExportInProgress',
      ExportComplete: 'ExportComplete',
      ExportFailed: 'ExportFailed',
      TransformationQueued: 'TransformationQueued',
      TransformationInProgress: 'TransformationInProgress',
      TransformationComplete: 'TransformationComplete',
      TransformationFailed: 'TransformationFailed',
      ImportQueued: 'ImportQueued',
      ImportInProgress: 'ImportInProgress',
      Complete: 'completed',
      SnapshotComplete: 'SnapshotCompleted',
      Failed: 'failed',
      // Rollback pipeline stages
      RollbackQueued: 'RollbackQueued',
      RollbackSetupInProgress: 'RollbackSetupInProgress',
      RollbackPlanAwaitingApproval: 'RollbackPlanAwaitingApproval',
      RollbackInProgress: 'RollbackInProgress',
      RollbackComplete: 'RollbackComplete',
      RollbackPartiallyCompleted: 'RollbackPartiallyCompleted',
      RollbackFailed: 'RollbackFailed'
    };

    // Check if status is a number
    if (typeof status === 'number') {
      const mapped = numericStatusMap[status];
      if (!mapped) {
        logger.warn('Unknown numeric migration status', { status });
      }
      return mapped || 'running';
    }

    // Check if status is a string
    if (typeof status === 'string') {
      const mapped = stringStatusMap[status];
      if (!mapped) {
        logger.debug('Unmapped string migration status', { status });
      }
      return mapped || status;
    }

    logger.warn('Invalid migration status type', { status, type: typeof status });
    return 'running';
  } catch (error) {
    logger.error('Error in mapMigrationStatus', {
      error: error instanceof Error ? error.message : String(error),
      status: String(status)
    });
    return 'running';
  }
}

/**
 * Gets a display-friendly label for a migration status
 */
export function getStatusLabel(status: string): string {
  try {
    const uiStatus = mapMigrationStatus(status);

    // Convert camelCase to Title Case with spaces
    return uiStatus
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  } catch (error) {
    logger.error('Error in getStatusLabel', {
      error: error instanceof Error ? error.message : String(error),
      status
    });
    return status;
  }
}
